import { FaqableType } from "./faq";

export interface PartnerOffering {
  id: string;
  name: string;
  slug: string;
  offeringTitle?: string;
  description: string;
  template: Template;
  partnerId: string;
  promoCodeBoxContent?: string;
  generalInfo?: string;
  spaceIds: string[];
  addOnIds: string[];
  lastUpdated: Date;
  faqs?: string;
  offeringLocations: PartnerOfferingLocationOverride[];
}

export interface PartnerOfferingLocationOverride {
  id: string;
  // Absent for Membership items, which have no space.
  spaceID?: string;
  faqs?: string;
  // Resolved server-side by the v3 serializer so the FAQ scope list needs no extra
  // spaces/locations lookup. "Space" → name is the space; "Location" → name is the venue.
  name?: string;
  locationName?: string;
  offerableType?: "Space" | "Location" | "Membership";
  productType?: string;
  locationId?: string;
  // What kind of space this entry stands for. `entire_location` marks the placeholder a
  // whole-venue product (day pass, multi pass) is modelled as — one per venue, never
  // individually bookable — so counts over these entries have to leave it out. Absent for a
  // Membership item, which has no space.
  spaceType?: string;
}

// Which FAQ a space shows on the catalog page, and which faqable owns it — for
// the read-only block plus the "Manage FAQ" deep-link. The v3 serializer emits a
// space-keyed override entry for every item (even when its FAQ is blank), so a
// space is always targetable: a blank override falls back to the offering default
// for display but still deep-links to the item, letting a partner customise that
// room. A space with no override entry deep-links to the offering default.
export interface FaqScope {
  html?: string;
  isCustom: boolean;
  faqableType: FaqableType;
  faqableId: string;
}

export function getFaqScopeForSpace(
  offering: PartnerOffering,
  spaceID: string
): FaqScope {
  const override = offering.offeringLocations.find(
    (l) => l.spaceID === spaceID
  );
  if (override) {
    return {
      html: override.faqs || offering.faqs,
      isCustom: Boolean(override.faqs),
      faqableType: "PartnerOfferingLocation",
      faqableId: override.id,
    };
  }
  return {
    html: offering.faqs,
    isCustom: false,
    faqableType: "PartnerOffering",
    faqableId: offering.id,
  };
}

// Which FAQ a membership detail page shows, and which faqable owns it. A membership
// item has no space, so it resolves by location instead: the v3 serializer emits one
// override entry per membership POL, with `locationId` set for a Location-scoped
// membership. Match this page's location; for a GeoMarket/SpacePartner membership (a
// single POL spanning many locations, so no `locationId`) fall back to that one POL;
// otherwise the offering default. Mirrors getMembershipOfferingLocationFromLocationId
// on the public page.
export function getFaqScopeForMembership(
  offering: PartnerOffering,
  locationID: string
): FaqScope {
  const memberships = offering.offeringLocations.filter(
    (l) => l.offerableType === "Membership"
  );
  const override =
    memberships.find((l) => l.locationId === locationID) ??
    (memberships.length === 1 ? memberships[0] : undefined);
  if (override) {
    return {
      html: override.faqs || offering.faqs,
      isCustom: Boolean(override.faqs),
      faqableType: "PartnerOfferingLocation",
      faqableId: override.id,
    };
  }
  return {
    html: offering.faqs,
    isCustom: false,
    faqableType: "PartnerOffering",
    faqableId: offering.id,
  };
}

// Distinct customer-facing venues a catalog spans, for the catalogs list card.
// Each offering-location entry resolves to one venue server-side (a Space item
// carries its space's location, a Location item the venue itself), and a Location
// item emits one entry per space — all sharing a `locationId` — so de-duping by
// `locationId` counts each venue once. `locationName` is only a fallback for the
// rare item with no id.
export function countCatalogLocations(offerings: PartnerOffering[]): number {
  const venues = new Set<string>();
  offerings.forEach((offering) => {
    offering.offeringLocations.forEach((location) => {
      const key = location.locationId ?? location.locationName;
      if (key) venues.add(key);
    });
  });
  return venues.size;
}

export interface SpaceOffering {
  id: string;
  name: string;
  description: string;
  price: number;
  addOnID: string;
  spaceID: string;

  displayName?: string;
  numberOfEntries?: number;
  daysToExpire?: number;
  // Staff-only add-on. The FM order editor keeps these visible (staff may apply
  // them); the customer checkout hides them.
  dashboardOnly?: boolean;
}

export enum Template {
  DayPass = "day_pass",
  MultiPass = "multi_pass",
  AddOn = "add_on",
  DailyAccess = "daily_access",
  DayOfficeMultiPass = "day_office_multi_pass",
  DayOffice = "day_office",
  MeetingRoom = "meeting_room",
  SnowflakeDayOfficeListing = "snowflake_day_office_listing",
  SnowflakeDayOffice = "snowflake_day_office",

  // V2 templates
  DailyOffice = "daily_office",
  DailyAccessV2 = "daily_access_v2",
  MeetingRoomV2 = "meeting_room_v2",
  AddOnV2 = "add_on_v2",
  EventSpaceV2 = "event_space_v2",
  RoomGalleryV2 = "room_gallery_v2",
  MembershipV2 = "membership_v2",
}

export enum CatalogType {
  DailyAccess = "daily_access",
  MeetingRooms = "meeting_room",
  DailyOffice = "daily_office",
  EventSpace = "event_space",
  RoomGallery = "room_gallery",
  Membership = "membership",
  NotSupport = "not_support",
}

export function getCatalogTypeByTemplate(template?: string) {
  switch (template) {
    case Template.DailyAccess:
    case Template.DailyAccessV2:
    case Template.DayPass:
      return CatalogType.DailyAccess;
    case Template.DayOffice:
    case Template.DailyOffice:
    case Template.SnowflakeDayOffice:
      return CatalogType.DailyOffice;
    case Template.MeetingRoom:
    case Template.MeetingRoomV2:
      return CatalogType.MeetingRooms;
    case Template.EventSpaceV2:
      return CatalogType.EventSpace;
    case Template.RoomGalleryV2:
      return CatalogType.RoomGallery;
    case Template.MembershipV2:
      return CatalogType.Membership;
    default:
      return CatalogType.NotSupport;
  }
}

export function toPartnerOffering(data: any): PartnerOffering {
  return {
    id: data.id,
    name: data.name,
    slug: data.slug,
    offeringTitle: data.offering_title,
    description: data.description,
    template: data.template,
    partnerId: data.partner_id,
    promoCodeBoxContent: data.promo_code_box_content,
    generalInfo: data.general_info,
    spaceIds: data.space_ids,
    addOnIds: data.add_on_ids,
    lastUpdated: new Date(data.updated_at * 1000),
    faqs: data.faqs,
    offeringLocations: (data.partner_offering_locations ?? []).map(
      (l: any): PartnerOfferingLocationOverride => ({
        id: l.id,
        spaceID: l.space_id ?? undefined,
        faqs: l.faqs,
        name: l.name,
        locationName: l.location_name,
        offerableType: l.offerable_type,
        productType: l.product_type,
        locationId: l.location_id,
        spaceType: l.space_type ?? undefined,
      })
    ),
  };
}

export function toSpaceOffering(data: any): SpaceOffering {
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    displayName: data.display_name,
    price: data.price,
    numberOfEntries: data.number_of_entries,
    daysToExpire: data.days_to_expire,
    spaceID: data.space_id,
    addOnID: data.add_on_id,
    dashboardOnly: data.dashboard_only,
  };
}

export function calculateDiscountPercentage(
  multiPass: SpaceOffering,
  singlePass?: SpaceOffering
): number {
  if (!singlePass) return 0;

  if (singlePass.addOnID === multiPass.addOnID) {
    return 0;
  }
  if (!multiPass.numberOfEntries) return 0;

  const originPrice = multiPass.numberOfEntries * singlePass.price;
  const discountedPrice = multiPass.price;
  const discountValue = (originPrice - discountedPrice) / originPrice;

  return Math.round(discountValue * 100);
}
